import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, FolderTree, Sparkles, RefreshCw } from "lucide-react";
import { Card, CardBody } from "../components/Card";
import { Button } from "../components/Button";
import { api } from "../lib/api";
import type { IntentSummary } from "../lib/me";

export default function Categories() {
  const navigate = useNavigate();
  const [intents, setIntents] = useState<IntentSummary[] | null>(null);

  useEffect(() => {
    api.get<IntentSummary[]>("/api/intents").then(setIntents).catch(() => setIntents([]));
  }, []);

  if (intents === null) {
    return (
      <div className="flex h-60 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Categories</h2>
          <p className="mt-1 text-sm text-slate-500 font-medium">
            The intent patterns Triage recognizes when classifying incoming emails.
          </p>
        </div>
        <Button onClick={() => navigate("/categories/new")} className="self-start sm:self-center">
          + Create Category
        </Button>
      </div>

      {intents.length === 0 ? (
        <Card>
          <CardBody>
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 border border-slate-200/60 text-slate-450">
                <FolderTree className="h-6 w-6" />
              </div>
              <p className="mt-4 text-base font-bold text-slate-900">No categories yet</p>
              <p className="mt-1.5 max-w-sm text-sm text-slate-500 font-medium leading-normal">
                Install a starter pack to get a working set of categories, or create your own custom category.
              </p>
              <div className="mt-5">
                <Button size="sm" onClick={() => navigate("/categories/new")}>
                  Create category
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-slate-100 bg-white">
            {intents
              .slice()
              .sort((a, b) => {
                const orderMap: Record<string, number> = { active: 1, draft: 2, archived: 3 };
                const orderA = orderMap[a.status] ?? 99;
                const orderB = orderMap[b.status] ?? 99;
                if (orderA !== orderB) {
                  return orderA - orderB;
                }
                return a.priority - b.priority;
              })
              .map((intent) => (
                <li key={intent.intentId}>
                  <button
                    onClick={() => navigate(`/categories/${intent.intentId}`)}
                    className="w-full text-left group flex items-start gap-4 p-6 hover:bg-slate-50/50 transition-colors select-none cursor-pointer"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 border border-brand-100/50 text-brand-650 shadow-2xs group-hover:bg-brand-100 transition-colors">
                      <Sparkles className="h-4.5 w-4.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2.5">
                        <h3 className="text-[15px] font-semibold text-slate-900 group-hover:text-brand-600 transition-colors truncate max-w-[280px] sm:max-w-md">
                          {intent.displayName}
                        </h3>
                        <StatusBadge status={intent.status} />
                        <span className="rounded-md bg-slate-100 border border-slate-200/40 px-2 py-0.5 text-xs font-medium text-slate-500">
                          Priority {intent.priority}
                        </span>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-xs font-medium text-slate-500 leading-relaxed">
                        {intent.description || "No description provided."}
                      </p>
                      <div className="mt-3.5 flex items-center flex-wrap gap-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                        <span>{intent.examples.length} examples</span>
                        <span>·</span>
                        <span>{intent.matchingRules.length} rules</span>
                        <span>·</span>
                        <span>{intent.slots.length} slots</span>
                        <span>·</span>
                        <span>threshold: {intent.confidenceThreshold.toFixed(2)}</span>
                      </div>
                    </div>
                    <ChevronRight className="mt-1.5 h-5 w-5 text-slate-300 group-hover:text-slate-655 transition-colors shrink-0" />
                  </button>
                </li>
              ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "active"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200/50"
      : status === "draft"
        ? "bg-amber-50 text-amber-700 border-amber-200/50"
        : "bg-slate-100 text-slate-650 border-slate-200/70";
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>{status}</span>;
}
